import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { VideoTrainingSection } from 'admin/interfaces'
import { TrialTask } from 'routes/trial/interfaces'
import { manageAddOns, upgradeSuccess } from 'services/product/actions'
import { getAccountSuccess, getInstagramConnectionMethod } from 'services/settings'
import { logoutAction, setTrialStatus } from 'services/shared/actions'
import { User } from 'shared/types'
import {
  checkSubscriptionSuccess,
  fetchTrainingVideoSections,
  getTrialTasks,
  getUserSubscription,
  getUserSuccess,
  trackTrainingEvent,
  updateIntroProgress,
  updateUserSubscriptionTier
} from './actions'

export const USER_STATE_VERSION = 'user:2'

export interface UserState {
  current: User | null
  trialTasks: TrialTask[]
  trainingVideos: VideoTrainingSection[]
  subscription?: any
}

const initialState: UserState = {
  current: null,
  trialTasks: [],
  trainingVideos: []
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearTrialTask: (state) => {
      state.trialTasks = []
    }
  },
  extraReducers: builder => {
    builder
      .addCase(updateIntroProgress.fulfilled, (state, action) => {
        if (state.current) {
          state.current.finishedSetup = action.payload
        }
      })
      .addCase(checkSubscriptionSuccess, (state, action) => {
        const { product, productInfo, roles, user } = action.payload
        const trialEndsAt = user.account.trialEndsAt || user.account.trialEndedAt

        if (state.current) {
          state.current.productInfo = { ...productInfo, updatedAt: new Date().toISOString() }
          state.current.product = product
          state.current.account = {
            ...user.account,
            trialEndsAt
          }
          state.current.isBlocked = user.isBlocked
          state.current.countryCode = user.countryCode

          if (roles && state.current.roles) {
            state.current.roles = { ...state.current.roles, ...roles }
          }
        }
      })
      .addCase(getUserSuccess, (state, action) => {
        const user = action.payload
        state.current = {
          ...user,
          account: {
            ...user.account,
            masterAdmin: user.account.masterAdmin.toString() as any,
            trialEndsAt: user.account.trialEndsAt || (user.account as any).trialEndedAt
          }
        }
      })
      .addCase(getTrialTasks.fulfilled, (state, action) => {
        state.trialTasks = action.payload
      })
      .addCase(trackTrainingEvent.fulfilled, (state, action) => {
        const { eventCode, value } = action.payload
        const increment = value || 1
        const task = state.trialTasks.find(task => task.code === eventCode)
        if (task) {
          task.value += increment
          task.completed = task.value >= task.maxValue
        }
      })
      .addCase(fetchTrainingVideoSections.fulfilled, (state, action) => {
        state.trainingVideos = action.payload
      })
      .addCase(setTrialStatus, (state, action) => {
        const { trial, status, hasPaymentInfo, chargifySubscriptionId } = action.payload
        if (state.current) {
          state.current.account.trial = trial
          state.current.account.status = status
          state.current.account.hasPaymentInfo = hasPaymentInfo
          state.current.account.chargifySubscriptionId = chargifySubscriptionId
        }
      })
      .addCase(getAccountSuccess, (state, action) => {
        if (state.current) {
          if (action.payload.isImpersonating) {
            state.current.account.masterAdmin = action.payload.subscription.masterAdmin.toString()
          }
          state.current.account.chargifySubscriptionId = action.payload.subscription.chargifySubscriptionId
          state.current.account.trialEndsAt = action.payload.trialEndsAt
          state.current.account.productDetails = action.payload.productDetails
        }
      })
      .addCase(upgradeSuccess, (state, action) => {
        const { newPlan, productInfo } = action.payload
        if (state.current) {
          state.current.product = newPlan
          state.current.productInfo = productInfo
        }
      })
      .addCase(getUserSubscription.fulfilled, (state, action) => {
        state.subscription = action.payload
      })
      .addCase(updateUserSubscriptionTier.fulfilled, (state, action: PayloadAction<any>) => {
        if (state.current) {
          const currency = action.payload.user.account.currency
          state.current.account.tier = action.payload.user.account.tier
          state.current.currency = currency
          state.current.account.currency = currency
        }
      })
      .addCase(logoutAction, state => {
        state.current = null
      })
      .addCase(getInstagramConnectionMethod.fulfilled, (state, action) => {
        if (state.current?.account.config) {
          state.current.account.config.igDirect = action.payload.igDirect
        } else if (state.current) {
          state.current.account.config = { igDirect: action.payload.igDirect }
        }
      })
      .addCase(manageAddOns.fulfilled, (state, action) => {
        const { adminLimit, aiImagesLimit, profilesLimit } = action.payload
        if (state.current?.account) {
          if (typeof adminLimit === 'number') {
            state.current.account.productDetails.adminLimit = adminLimit
          }
          if (typeof aiImagesLimit === 'number') {
            state.current.account.productDetails.aiImagesLimit = aiImagesLimit
          }
          if (typeof profilesLimit === 'number') {
            state.current.account.productDetails.profilesLimit = profilesLimit
          }
        }
      })
  }
})

export const { clearTrialTask } = userSlice.actions
export default userSlice.reducer
